import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType
} from "react-router-dom";

import AppSettings from "./AppSettings";
import { createSentryAnonymousUser } from "./sentryUser";

/**
 * Initializes telemetry on module evaluation only when a truthy DSN is configured.
 *
 * Preconditions: Browser location APIs are available. Initialization failures are not caught here.
 * Postconditions: Successful setup registers integrations, tags and an anonymous identity.
 * Feedback is conditional; replay and tracing retain the configured capture and sampling options.
 */
if (AppSettings.sentry.dsn) {
    const integrations = [
        // React Router V7 integration.
        // Helps with tracing URL changes and which URLs belong to the
        // same route, for example:
        //    - https://test.com/form/1
        //    - https://test.com/form/2
        // both belong to the same route */form/*
        Sentry.reactRouterBrowserTracingIntegration({
            useEffect: useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes
        }),
        // Browser Tracing integration
        // Helps with tracing URLs and other common browser "spans"
        Sentry.browserTracingIntegration(),
        // Replay integration
        // Records user actions and sends to Sentry.
        Sentry.replayIntegration({
            // Whether to mask all text on the page when recording user actions
            // When false only inputs are masked.
            // There is a separate setting to force unmasked inputs.
            maskAllText: false,
            // Whether to mask all images on the page when recording user
            // actions.
            blockAllMedia: false,
            // Which URLs can the replay run on.
            // URL must contain one of the strings in the array or match one of
            // the regexes supplied
            networkDetailAllowUrls: [window.location.origin],
            // Capture the body of network requests
            // Sentry will still run data scrubbing on the bodies.
            networkCaptureBodies: true
        })
    ];
    if (AppSettings.sentry.enableReportABug) {
        integrations.push(
            Sentry.feedbackIntegration({
                // Disable the injection of the default widget
                autoInject: false,
                colorScheme: "system",
                showBranding: false,
                showName: false,
                showEmail: false,
                tags: {
                    Application: AppSettings.sentry.application,
                    Customer: AppSettings.sentry.customer
                },
                useSentryUser: {
                    name: "fullName",
                    email: "email"
                }
            })
        );
    }
    Sentry.init({
        // The DSN to use when sending information to Sentry.
        // Identifies a specific project in Sentry.
        dsn: AppSettings.sentry.dsn,
        // Whether to send identifying information to Sentry such as
        // username, useragent, ip, etc
        dataCollection: {
        },
        // Whether to attach stacktraces to actions sent to Sentry.
        attachStacktrace: AppSettings.sentry.attachStacktrace,
        // Whether @sentry/react is in debug mode, logging more information
        // to the console.
        debug: AppSettings.sentry.debug,
        // initialise integrations.
        integrations: integrations,
        // The environment to associate all action sent to Sentry with.
        environment: AppSettings.sentry.environment,
        // Release to associate all actions sent to Sentry with.
        release: AppSettings.sentry.release,
        // Percentage of captured traces that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        tracesSampleRate: AppSettings.sentry.tracesSampleRate,
        // Which URLs to trace / record in a trace.
        // Only trace internal URLs don't want to trace every SEO url
        // under the sun.
        tracePropagationTargets: ["localhost", /^\//, new RegExp(`/^${window.location.origin}`)],
        // Percentage of captured session replays that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        replaysSessionSampleRate: AppSettings.sentry.replaysSessionSampleRate,
        // Percentage of captured session replays that are sent to Sentry.
        // when an error occurs
        // 1.0 = 100%, 0 = 0%
        replaysOnErrorSampleRate: AppSettings.sentry.replaysOnErrorSampleRate
    });

    Sentry.setTag("Application", AppSettings.sentry.application);
    Sentry.setTag("Customer", AppSettings.sentry.customer);

    // Set user to anonymous at start up, this should be overriden by
    // React code if user information is available.
    Sentry.setUser(createSentryAnonymousUser());
}
